import { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';

const REFRESH_MS = 15000; // auto-refresh every 15s

/**
 * Loads the transactions list for the dashboard and tracks connection state.
 * (ANPR pending-review polling removed — those endpoints no longer exist.)
 *
 * Returns exactly what App/Header consume:
 *   transactions, loading, refreshing, lastRefresh, apiOnline, reload
 */
export function useGateData() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]       = useState(true);   // first load only
  const [refreshing, setRefreshing] = useState(false);  // background refreshes
  const [apiOnline, setApiOnline]   = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const firstLoad = useRef(true);

  const reload = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await gateApi.getTransactions({ page: 0, size: 50 });
      // tolerate both the new envelope and a plain array (older backend)
      const items = res?.items ?? (Array.isArray(res) ? res : []);
      setTransactions(items);
      setApiOnline(true);
      setLastRefresh(new Date());
    } catch (e) {
      // a 401 is handled by the axios interceptor (redirects to /login);
      // anything else just means the API is unreachable right now
      setApiOnline(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  return { transactions, loading, refreshing, lastRefresh, apiOnline, reload };
}

export default useGateData;